import React, { useEffect, useState, useContext } from "react";
import { statusApi, handleApiError } from "../../utils/api";
import { AuthContext } from "../../contexts/AuthContext";

const EditStatusForm = ({ status, onCancel, onUpdated }) => {
  const [formData, setFormData] = useState({ ...status });
  const [statusTypes, setStatusTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { user, isAuthenticated } = useContext(AuthContext);

  useEffect(() => {
    if (user) {
      fetchStatusTypes();
    }
  }, [user]);

  useEffect(() => {
    // Track if form has changes
    const changed =
      formData.status !== status.status ||
      formData.status_description !== status.status_description ||
      formData.status_type_id !== status.status_type_id;
    setHasChanges(changed);
  }, [formData, status]);

  const fetchStatusTypes = async () => {
    try {
      // HIPAA Compliant: Using statusApi with secure httpOnly cookie auth
      const response = await statusApi.getTypes();
      setStatusTypes(response.data);
    } catch (err) {
      console.error("❌ Failed to load status types", err);
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!formData.status.trim()) {
      setError("Status name is required");
      return;
    }

    if (!formData.status_type_id) {
      setError("Status type is required");
      return;
    }

    if (!hasChanges) {
      setError("No changes to save");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // HIPAA Compliant: Using statusApi with secure httpOnly cookie auth
      await statusApi.update(formData.status_id, {
        status: formData.status.trim(),
        status_description: formData.status_description?.trim() || "",
        status_type_id: formData.status_type_id,
        // updated_by is handled server-side from the authenticated user
      });

      // Success notification
      alert("✅ Status updated successfully!");

      // Notify parent component
      if (onUpdated) {
        onUpdated();
      }
    } catch (err) {
      console.error("❌ Error updating status", err);
      const apiError = handleApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  // Check authentication
  if (!user) {
    return (
      <div className="alert alert-warning" role="alert">
        Please log in to edit statuses.
      </div>
    );
  }

  // Check authorization
  if (user?.role !== "admin") {
    return (
      <div className="alert alert-info" role="alert">
        Only administrators can edit statuses.
      </div>
    );
  }

  return (
    <div>
      <h4>Edit Status</h4>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3 mt-3">
        <div className="col-md-6">
          <label htmlFor="edit-status-name" className="form-label">
            Status Name <span className="text-danger">*</span>
          </label>
          <input
            id="edit-status-name"
            className="form-control"
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
            maxLength={100}
            placeholder="Enter status name"
            disabled={loading}
          />
        </div>

        <div className="col-md-6">
          <label htmlFor="edit-status-type" className="form-label">
            Status Type <span className="text-danger">*</span>
          </label>
          <select
            id="edit-status-type"
            className="form-select"
            name="status_type_id"
            value={formData.status_type_id || ""}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Select Type</option>
            {statusTypes.map((type) => (
              <option key={type.status_type_id} value={type.status_type_id}>
                {type.status_type_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-12">
          <label htmlFor="edit-status-description" className="form-label">
            Description
          </label>
          <textarea
            id="edit-status-description"
            className="form-control"
            name="status_description"
            value={formData.status_description || ""}
            onChange={handleChange}
            rows="3"
            maxLength={500}
            placeholder="Enter status description (optional)"
            disabled={loading}
          />
          <div className="form-text">
            {(formData.status_description || "").length}/500 characters
          </div>
        </div>

        {/* Display audit information - HIPAA compliance */}
        <div className="col-12">
          <div className="card bg-light">
            <div className="card-body py-2">
              <small className="text-muted">
                <strong>Audit Trail:</strong>
                <br />
                Created: {new Date(formData.created_at).toLocaleString()}
                by {formData.created_by || "system"}
                {formData.updated_at &&
                  formData.updated_at !== formData.created_at && (
                    <>
                      <br />
                      Last updated:{" "}
                      {new Date(formData.updated_at).toLocaleString()}
                      by {formData.updated_by || "system"}
                    </>
                  )}
              </small>
            </div>
          </div>
        </div>

        <div className="col-12 d-flex justify-content-between">
          <button
            className="btn btn-secondary"
            type="button"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <div>
            <button
              className="btn btn-outline-secondary me-2"
              type="button"
              onClick={() => setFormData({ ...status })}
              disabled={loading || !hasChanges}
            >
              Reset
            </button>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={
                loading ||
                !formData.status.trim() ||
                !formData.status_type_id ||
                !hasChanges
              }
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Audit info for HIPAA compliance */}
      <div className="mt-3">
        <small className="text-muted">
          <i className="bi bi-info-circle"></i> All changes are logged for audit
          purposes.
        </small>
      </div>
    </div>
  );
};

export default EditStatusForm;
